import { Session } from '../../machine/types'
import { AdapterInitResult, IdPAdapter } from '../IdPAdapter'
import { ClaimMapperOptions, claimsFromKeycloakToken } from './parseClaims'

/**
 * Subset of `react-native-app-auth` we depend on. The mobile app injects
 * the real lib at construction time; the package stays platform-agnostic
 * so web typecheck doesn't pull in RN types.
 */
export interface RNAppAuthLib<TConfig = unknown> {
    authorize: (config: TConfig) => Promise<RNAppAuthAuthorizeResult>
    refresh: (config: TConfig, params: { refreshToken: string }) => Promise<RNAppAuthRefreshResult>
    logout?: (
        config: TConfig,
        params: { idToken: string; postLogoutRedirectUrl: string },
    ) => Promise<void>
}

export interface RNAppAuthAuthorizeResult {
    accessToken: string
    refreshToken?: string
    idToken?: string
    accessTokenExpirationDate?: string
    tokenAdditionalParameters?: Record<string, string>
}

export type RNAppAuthRefreshResult = RNAppAuthAuthorizeResult

/** JWT-decode contract — apps inject `jwt-decode`'s default export. */
export type JwtDecode = <T>(token: string) => T

export interface KeycloakMobileAdapterOptions extends ClaimMapperOptions {
    /** OAuth/OIDC config passed straight through to `react-native-app-auth`. */
    config: unknown
    /** Injected `react-native-app-auth` lib (host owns the native dep). */
    rnAppAuth: RNAppAuthLib
    /** Injected JWT decoder. */
    jwtDecode: JwtDecode
    /** Required when calling `logout()` to hit the IdP. */
    postLogoutRedirectUrl?: string
}

const toSession = (
    result: RNAppAuthAuthorizeResult,
    options: KeycloakMobileAdapterOptions,
): Session => {
    if (!result.accessToken) {
        throw new Error('react-native-app-auth returned a result without an access token')
    }
    const parsed = options.jwtDecode<Record<string, unknown>>(result.accessToken)
    return {
        accessToken: result.accessToken,
        refreshToken: result.refreshToken,
        idToken: result.idToken,
        claims: claimsFromKeycloakToken(parsed, options),
    }
}

// `token_refresh_failed` is deliberately absent: react-native-app-auth uses it
// as the *fallback* code whenever a refresh rejects without an OAuth error body
// (iOS `rejectPromise:defaultCode:@"token_refresh_failed"`, Android
// `handleAuthorizationException("token_refresh_failed", …)` when `ex.error` is
// null), so every network / DNS / timeout failure carries it. Treating it as
// terminal wiped the persisted session on an offline launch. A genuine
// rejection of the refresh token arrives as `invalid_grant`, which is listed.
const RNAUTH_ERROR_CODES_TERMINAL = new Set([
    'invalid_grant',
    'invalid_client',
    'unauthorized_client',
    'invalid_scope',
    'auth_canceled', // user cancelled — treat as terminal so the FSM lands in reauthRequired
])

/**
 * `IdPAdapter` wrapping `react-native-app-auth` (Keycloak via OIDC).
 *
 * - `init()` returns no session — mobile relies on the runtime's storage
 *   adapter for session rehydration, then explicit `login()` when none
 *   is restored.
 * - `login()` triggers the native browser auth flow.
 * - `refresh()` uses the OIDC refresh token grant.
 * - `logout()` hits the IdP's end-session endpoint (when supported).
 */
export const createKeycloakMobileAdapter = (options: KeycloakMobileAdapterOptions): IdPAdapter => {
    const { config, rnAppAuth } = options

    const init = async (): Promise<AdapterInitResult> => ({
        session: null,
        supported: true,
    })

    const login = async (): Promise<Session> => {
        const result = await rnAppAuth.authorize(config)
        return toSession(result, options)
    }

    const refresh = async (refreshToken: string | undefined): Promise<Session> => {
        if (!refreshToken) {
            throw new Error('No refresh token available')
        }
        const result = await rnAppAuth.refresh(config, { refreshToken })
        return toSession(result, options)
    }

    const logout = async (idToken: string | undefined): Promise<void> => {
        if (!rnAppAuth.logout || !idToken || !options.postLogoutRedirectUrl) {
            return
        }
        try {
            await rnAppAuth.logout(config, {
                idToken,
                postLogoutRedirectUrl: options.postLogoutRedirectUrl,
            })
        } catch {
            // best-effort; the runtime clears local state regardless
        }
    }

    const isAuthError = (err: unknown): boolean => {
        if (!err) {
            return false
        }
        // react-native-app-auth uses `code` as the structured field. Match
        // strictly first; only fall back to substring scanning on `message`
        // when no structured code is present, so a generic server error
        // whose text happens to contain `invalid_grant` isn't classified
        // as a terminal auth failure.
        const e = err as { code?: string; message?: string }
        const code = typeof e.code === 'string' ? e.code.toLowerCase() : undefined
        if (code) {
            return RNAUTH_ERROR_CODES_TERMINAL.has(code)
        }
        const message = typeof e.message === 'string' ? e.message.toLowerCase() : undefined
        if (!message) {
            return false
        }
        return Array.from(RNAUTH_ERROR_CODES_TERMINAL).some(c => message.includes(c))
    }

    const parseClaims = (accessToken: string) => {
        const parsed = options.jwtDecode<Record<string, unknown>>(accessToken)
        return claimsFromKeycloakToken(parsed, options)
    }

    return {
        name: 'keycloak-mobile',
        init,
        login,
        refresh,
        logout,
        isAuthError,
        parseClaims,
    }
}
